import React, { useEffect, useRef, useState } from 'react';
import StepSheet from '../../components/nucleus/StepSheet';
import { Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import Switch from '../../components/nucleus/Switch';
import { useConfirm } from '../../components/nucleus/actions';
import { dateOf, plural } from '../../nucleus/format';
import { blank } from './fields';
import { pluginsApi } from './data/api';
import { frequencyWord } from './data/words';
import { progressWord } from './data/inspector';
import {
    AccountPart, CredentialsPart, SettingsPart, useDisconnect, saveCredentials, saveSettings, settingValues,
} from './PluginSetup';
import { AsksFor, ChangeRows } from './ApprovalParts';
import './plugin-inspector.css';

// SET UP ‹PLUGIN› — the guided making sheet (the RealmsEditor pattern, drawn by
// StepSheet): the steps outlined at the left with how far along it is ("2 of 3
// done"), one at a time at the right under its number and its one instruction
// line, and the foot's Back, Next and the step's act. Sign in · Keys ·
// Settings · Turn on: only the steps the plugin needs. The bar's primary opens
// it at the first step still missing, /plugins/<id>/setup the same,
// /plugins/<id>/consent at Turn on. A save the server holds for its yes (202
// consent_required) goes on to Turn on. When the last step is done, it closes
// (`onReady`). How often each tool runs automatically is the tool's (its row,
// its sheet's Runs automatically tab), not setup's.

export const STEP_IDS = ['account', 'credentials', 'settings', 'approve'];

const RUNNING = ['running', 'failing'];

function AskEveryRun({ setup, asks, tools, gate, pluginId, groupId, onChanged }) {
    const confirm = useConfirm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const developer = !!asks?.requiresConsent;
    const on = developer || !!setup?.require_consent_per_run;
    const running = tools.filter((t) => RUNNING.includes(t.schedule?.state)).length;
    const stops = running === 1 ? 'Its tool can’t run automatically while this is on' : `Its ${plural(running, 'tool')} can’t run automatically while this is on`;
    const flip = async (next) => {
        if (next && running > 0) {
            const ok = await confirm({
                title: 'Ask every run?', body: `${stops}.`, label: 'Ask every run', danger: false,
            });
            if (!ok) return;
        }
        setBusy(true); setError(null);
        try {
            await pluginsApi.save(pluginId, groupId, { require_consent_per_run: next });
            onChanged?.(next ? 'It asks you before every run.' : 'It runs without asking each time.');
        } catch (e) { setError(e?.message || 'Could not save'); } finally { setBusy(false); }
    };
    return (
        <div className="plugins-switchline">
            <label className="plugins-switchline__text" htmlFor="plugins-ask-every-run">
                <span>Ask every run</span>
                {developer && <small>Its developer asks every run</small>}
                {!developer && running > 0 && <small>{stops}</small>}
            </label>
            <Switch id="plugins-ask-every-run" aria-label="Ask every run" checked={on} disabled={developer || busy || !gate.isAdmin}
                title={gate.isAdmin ? undefined : gate.hint} onChange={(e) => flip(e.target.checked)} />
            {error && <p className="plugins-error" role="alert">{error}</p>}
        </div>
    );
}

/** Runs automatically that lost its tool: each can be given to a tool, or turned off. */
export function LooseSchedules({ loose, tools, gate, pluginId, groupId, onChanged }) {
    const [error, setError] = useState(null);
    const put = async (body, words) => {
        setError(null);
        try { await pluginsApi.save(pluginId, groupId, body); onChanged?.(words); } catch (e) { setError(e?.message || 'Could not save'); throw e; }
    };
    const named = tools.filter((t) => t.id);
    return (
        <>
            {loose.map((s) => (
                <Row key={s.id} name={s.name || 'Runs automatically'}
                    sub={[s.frequency || s.cron ? frequencyWord(s.frequency || s.cron) : null, s.disabled_text || (s.enabled ? 'On' : 'Off')].filter(Boolean).join(' · ')}
                    actions={[
                        ...named.map((t) => ({
                            id: `assign-${t.id}`, label: `Give it to ${t.name}`, disabled: !gate.isAdmin, hint: gate.hint,
                            run: () => put({ automation_id: s.id, tool_id: t.id }, `${t.name} now runs automatically.`),
                        })),
                        s.enabled && {
                            id: 'switch-off', label: 'Turn off', disabled: !gate.isAdmin, hint: gate.hint,
                            run: () => put({ automation_id: s.id, enabled: false }, `${s.name || 'It'} is off.`),
                        },
                    ].filter(Boolean)} />
            ))}
            {error && <p className="plugins-error" role="alert">{error}</p>}
        </>
    );
}

export default function SetupSheet({
    name, pluginId, groupId, setup, oauth, credentials, groups, reenter = [], publicKey, asks, nameOf, approval, steps: checks,
    tools, start, gate, onConnect, onApprove, onChanged, onClose, onReady,
}) {
    const [at, setAt] = useState(start || null);
    const [creds, setCreds] = useState({});
    // each settings group's values, by its index
    const [sets, setSets] = useState(() => groups.map(settingValues));
    // a reload that changes the groups starts their values again from what is saved
    const sig = groups.map((g) => g.sig).join('||');
    useEffect(() => { setSets(groups.map(settingValues)); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
    const disconnect = useDisconnect({ pluginId, groupId, name, oauth, onChanged });

    const check = (id) => checks.find((c) => c.id === id) || { status: 'not_needed' };
    const stateOf = (id) => ({ done: 'done', missing: 'left', not_needed: 'skipped' }[check(id).status] || 'left');
    const held = gate.isAdmin ? null : gate.hint;
    const provider = oauth?.provider_label || 'its provider';
    const conn = oauth?.connection || null;
    const needsApproval = check('approve').status === 'missing';
    const order = STEP_IDS;
    const after = (id) => {
        for (let i = order.indexOf(id) + 1; i < order.length; i += 1) if (stateOf(order[i]) !== 'skipped') return order[i];
        return null;
    };
    // THE LAST STEP DONE: a sheet opened with something still missing closes
    // once nothing is (the lifecycle read again says so), and says it is ready
    const missing = checks.some((c) => c.status === 'missing');
    const opened = useRef(missing);
    useEffect(() => {
        if (opened.current && !missing) { opened.current = false; onReady?.(); }
    }, [missing]); // eslint-disable-line react-hooks/exhaustive-deps
    // a save held for the approval goes there; anything else, on to the next step
    const then = (id, approve) => setAt(approve ? 'approve' : after(id));
    const typed = Object.values(creds).some((v) => !blank(v));

    const steps = [
        {
            id: 'account', title: 'Sign in', state: stateOf('account'), say: check('account').sub,
            instruction: `Sign in to ${provider} so ${name} can reach your ${provider} data.`,
            content: <AccountPart oauth={oauth} gate={gate} onDisconnect={async () => { if (await disconnect()) setAt('account'); }} />,
            act: {
                label: conn?.connected || conn?.revoked ? 'Sign in again' : 'Sign in',
                primary: !conn?.connected,
                run: onConnect, advance: false, disabled: !gate.isAdmin, hint: held,
            },
        },
        {
            id: 'credentials', title: 'Keys', state: stateOf('credentials'), say: check('credentials').sub,
            instruction: `Paste what ${name} gives you. They’re locked to ${name} in this browser — ofSelf can’t read them.`,
            content: (
                <CredentialsPart pluginId={pluginId} groupId={groupId} fields={credentials} values={creds} reenter={reenter} publicKey={publicKey} gate={gate}
                    onValue={(k, v) => setCreds((cur) => ({ ...cur, [k]: v }))} onCleared={onChanged} />
            ),
            act: !publicKey
                ? { label: 'Save keys', disabled: true, hint: 'It has no encryption key, so nothing can be saved for it.' }
                : (typed || check('credentials').status !== 'done') ? {
                    label: 'Save keys', disabled: !typed || !gate.isAdmin, hint: held, advance: false,
                    run: async () => {
                        const { approve } = await saveCredentials(pluginId, groupId, credentials, creds, publicKey);
                        setCreds({});
                        onChanged?.('Keys saved.');
                        then('credentials', approve);
                    },
                } : null,
        },
        {
            id: 'settings', title: 'Settings', state: stateOf('settings'), say: check('settings').sub,
            instruction: 'Answer these once. A run can still change its own inputs.',
            content: groups.map((g, i) => (
                <SettingsPart key={g.sig} pluginId={pluginId} groupId={groupId} group={g} gate={gate}
                    heading={groups.length > 1 ? g.names.join(', ') : null} values={sets[i] || {}}
                    onValue={(k, v) => setSets((cur) => cur.map((x, j) => (j === i ? { ...x, [k]: v } : x)))} onCleared={onChanged} />
            )),
            act: {
                label: 'Save settings', disabled: !gate.isAdmin, hint: held, advance: false,
                run: async () => {
                    let approve = false;
                    for (let i = 0; i < groups.length; i += 1) {
                        // eslint-disable-next-line no-await-in-loop
                        const r = await saveSettings(pluginId, groupId, groups[i], sets[i] || {});
                        approve = approve || r.approve;
                    }
                    onChanged?.('Settings saved.');
                    then('settings', approve);
                },
            },
        },
        {
            id: 'approve', title: 'Turn on', state: stateOf('approve'), say: check('approve').sub,
            instruction: approval?.changed && needsApproval
                ? 'It changed since you turned it on. Read what’s new, then turn it on again.'
                : 'Read what it can do. When you turn it on, its tools can run.',
            content: (
                <>
                    {approval?.changed && (
                        <div className="plugins-changed">
                            <span className="plugins-sub">What changed</span>
                            <ChangeRows approval={approval} tools={asks?.tools || []} />
                        </div>
                    )}
                    <div>
                        <span className="plugins-sub">What it can do</span>
                        <AsksFor asks={asks} nameOf={nameOf} />
                    </div>
                    <AskEveryRun setup={setup} asks={asks} tools={tools} gate={gate} pluginId={pluginId} groupId={groupId} onChanged={onChanged} />
                    {!needsApproval && approval?.approvedAt && <Note>{`Turned on ${dateOf(approval.approvedAt)}${approval.approvedBy ? ` by ${approval.approvedBy}` : ''}.`}</Note>}
                </>
            ),
            act: needsApproval ? {
                label: 'Turn on', disabled: !gate.isAdmin, hint: held,
                run: async () => { await onApprove(); },
            } : null,
        },
    // only the steps it needs (one it is already at stays, so a door to it still lands)
    ].filter((st) => st.state !== 'skipped' || st.id === at);

    return (
        <StepSheet
            label={`Set up ${name}`} head={progressWord(checks)} title={`Set up ${name}`}
            steps={steps} at={at || undefined} onAt={setAt} start={start || undefined} onClose={onClose} doneLabel="Done" className="plugins-setup"
        />
    );
}

